import { Link, useParams } from 'react-router-dom'
import { useClusterById } from '@/hooks/use-cluster-by-id'
import { ChevronLeft } from 'lucide-react'
import { useEffect } from 'react'
import { PageContainer } from '@/component/layout'

export const ClusterDetail = () => {
  const { id } = useParams<{ id: string }>()
  const { item, loading, error, associatedModalities } = useClusterById(id)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  if (!id) return <div className='p-6'>Missing id</div>
  if (loading) return <div className='p-6 text-center'>Loading cluster…</div>
  if (error) return <div className='p-6 text-red-600'>Error: {error}</div>
  if (!item) return <div className='p-6'>Not found</div>

  const title = item.name ?? `Cluster #${id}`

  return (
    <PageContainer>
      {/* Back link */}
      <div className='mb-3'>
        <Link to='/clusters' className='inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900'>
          <span className='-ml-1'>
            <ChevronLeft />
          </span>
          Back to cluster mappings
        </Link>
      </div>

      {/* Header */}
      <div className='mb-6'>
        <h1 className='text-3xl font-bold'>{title}</h1>
      </div>

      {/* Summary */}
      <DetailSection title='Summary' tooltip='A brief overview of this learning cluster and its core characteristics.' content={item.description || '—'} />

      {/* Long Form Descriptor */}
      {item.longFormDescriptor && (
        <DetailSection
          title='Long Form Descriptor'
          tooltip='Detailed explanation of how this cluster centers on the application of knowledge in authentic or simulated environments.'
          content={item.longFormDescriptor}
        />
      )}

      {/* Instructional Design Features */}
      {item.instructionalDesignFeatures && (
        <DetailSection
          title='Instructional Design Features'
          tooltip='Key design elements and methodologies used in this learning cluster.'
          content={item.instructionalDesignFeatures}
        />
      )}

      {/* Empirical Support */}
      {item.empiricalSupport && (
        <DetailSection
          title='Empirical Support'
          tooltip='Research evidence and studies supporting the effectiveness of this learning approach.'
          content={item.empiricalSupport}
        />
      )}

      {/* Learner Outcomes */}
      {item.learnerOutcomes && (
        <DetailSection
          title='Learner Outcomes'
          tooltip='Expected learning outcomes and benefits for students using this approach.'
          content={item.learnerOutcomes}
        />
      )}

      {/* Suitability for Diverse Learners */}
      {item.suitabilityForDiverseLearners && (
        <DetailSection
          title='Suitability for Diverse Learners'
          tooltip='How this learning cluster accommodates different learning preferences and needs.'
          content={item.suitabilityForDiverseLearners}
        />
      )}

      {/* Exemplar Research Paper */}
      {item.exemplarResearchPaper && (
        <DetailSection
          title='Exemplar Research Paper'
          tooltip='Key research paper that exemplifies or supports this learning cluster approach.'
          content={item.exemplarResearchPaper}
        />
      )}

      {/* Associated Modalities */}
      {associatedModalities && associatedModalities.length > 0 && (
        <div className='mb-6 rounded-xl border border-gray-200 bg-white p-5'>
          <div className='mb-4 flex items-center gap-2'>
            <div className='text-sm font-semibold text-gray-900'>Associated Modalities</div>
            <span title='Learning modalities that are mapped to this cluster'>{/* <Info className='h-4 w-4 text-gray-400' /> */}</span>
          </div>
          <div className='space-y-2'>
            {associatedModalities.map((modality: any) => (
              <div key={modality.id} className='flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100'>
                <div className='flex-1'>
                  <Link to={`/modalities/${modality.id}`} className='font-medium text-blue-700 hover:underline'>
                    {modality.name}
                  </Link>
                  {modality.description && <p className='text-sm text-gray-600 mt-1'>{modality.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </PageContainer>
  )
}

/* ---------- UI helpers ---------- */

function DetailSection({ title, tooltip, content }: { title: string; tooltip?: string; content: string }) {
  return (
    <div className='mb-6 rounded-xl border border-gray-200 bg-white p-5'>
      <div className='mb-2 flex items-center gap-2'>
        <div className='text-sm font-semibold text-gray-900'>{title}</div>
        {tooltip && <span title={tooltip}>{/* <Info className='h-4 w-4 text-gray-400' /> */}</span>}
      </div>
      <p className='text-gray-800 leading-relaxed whitespace-pre-wrap'>
        {content && String(content).trim().length ? content : <span className='text-gray-400'>—</span>}
      </p>
    </div>
  )
}
